/**
 * StaffingMaske (#158, Spec Abschnitt 5; #163: die Position ist der Bedarf): Positionen und
 * Besetzungen eines Projekts in einer Komponente. Lädt selbst (`['staffing-maske', projectId]`), wenn `maske` nicht übergeben
 * wird (KL-Board liefert den Baum gesammelt mit). Nach jeder Änderung werden Maske und KL-Board
 * neu geladen (`maske-kontext.tsx`).
 *
 * Rechte (`useStaffingPermissions`): Positionsfelder und die KI-Ableitung (F5) `canManage`; alles an Besetzungen `canResolve`; „ausplanen" `canAusplanen`.
 * Ohne Rechte nur Anzeige.
 *
 * #163: KI-Vorschläge stehen nur im Kasten „zu bestätigen" ganz oben, nie in der normalen
 * Positionsliste (kein Ablageziel, kein „+ Person besetzen"). #160: Ist die Maske ganz leer, zeigt
 * sie mit `canManage` einen deutlichen Knopf „+ Position", sonst den Hinweis auf den Staffing-Kommentar.
 */
import type { StaffingMaske as StaffingMaskeDaten } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { useStaffingPermissions } from '../../../lib/permissions.js';
import { BesetzungZeile } from './besetzung-zeile.js';
import { KiAbleitungKnopf } from './ki-ableitung.js';
import {
  MaskeKontext,
  type MaskeKontextWert,
  type MaskeOption,
  usePreisText,
} from './maske-kontext.js';
import { NichtZugeordnet, ZiehBereich } from './nicht-zugeordnet.js';
import { positionOptionen } from './position-auswahl.js';
import { NeuePositionZeile, PositionsZeile } from './positions-zeile.js';
import { ZuBestaetigenKasten } from './zu-bestaetigen-kasten.js';

export type StaffingMaskeProps = {
  projectId: string;
  project: Parameters<typeof useStaffingPermissions>[0];
  maske?: StaffingMaskeDaten | undefined;
};

export function StaffingMaske({ projectId, project, maske }: StaffingMaskeProps) {
  const { t } = useTranslation();
  const rechte = useStaffingPermissions(project);
  const preisText = usePreisText();
  const query = useQuery({
    queryKey: ['staffing-maske', projectId],
    queryFn: () => api.getStaffingMaske(projectId),
    enabled: !maske,
  });
  const daten = maske ?? query.data;
  const [neuePositionen, setNeuePositionen] = useState<number[]>([]);
  // Eben angelegte Positionen erscheinen nach dem Neuladen aufgeklappt (Final-Fix T11).
  const [aufgeklappt, setAufgeklappt] = useState<string[]>([]);
  const [zaehler, setZaehler] = useState(0);
  const [ehemaligeOffen, setEhemaligeOffen] = useState(false);
  const neuePosition = () => {
    setNeuePositionen((l) => [...l, zaehler]);
    setZaehler((z) => z + 1);
  };

  const kontext = useMemo<MaskeKontextWert | null>(() => {
    if (!daten) return null;
    // F1: unterscheidbar (Stufe · Satz · PT, Gleiches durchnummeriert) — eine Stelle für alle Auswahlen.
    // #163: nur bestätigte Positionen sind Ziel einer Besetzung (KI-Vorschlag → 400 position_unbestaetigt).
    const bestaetigte = daten.positionen.filter((p) => p.status === 'bestaetigt');
    const positionAuswahl: MaskeOption[] = positionOptionen(bestaetigte, {
      preis: preisText,
      pt: (pt) => t('v2.staffing_maske.pt', { n: pt === null ? '—' : pt.toLocaleString('de-DE') }),
    });
    // Final-Fix I4: Kandidaten, um eine SF-Projektbesetzung einer bestehenden Besetzung zuzuordnen.
    const verknuepfbareBesetzungen: MaskeOption[] = [
      ...daten.positionen.flatMap((p) => p.besetzungen.map((b) => ({ b, wo: p.stufe }))),
      ...daten.nicht_zugeordnet.besetzungen.map((b) => ({ b, wo: null })),
    ]
      .filter(({ b }) => b.status !== 'ausgeplant' && b.sf_projektbesetzung_id === null)
      .map(({ b, wo }) => ({
        value: b.staffing_besetzung_id,
        label: wo ? `${b.person_name} · ${wo}` : b.person_name,
      }));
    return {
      projectId,
      rechte,
      positionOptionen: positionAuswahl,
      verknuepfbareBesetzungen,
    };
  }, [daten, projectId, rechte, t, preisText]);

  // #163: die normale Liste zeigt nur bestätigte Positionen; KI-Vorschläge stehen im Kasten.
  const bestaetigte = daten?.positionen.filter((p) => p.status === 'bestaetigt') ?? [];
  // #160: nichts da — weder Position (auch kein Vorschlag) noch etwas „nicht zugeordnet".
  const ganzLeer =
    !!daten &&
    daten.positionen.length === 0 &&
    daten.nicht_zugeordnet.besetzungen.length === 0 &&
    daten.nicht_zugeordnet.sf_projektbesetzungen.length === 0 &&
    neuePositionen.length === 0;

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          {t('v2.staffing_maske.ueberschrift', 'Positionen & Besetzungen')}
        </h3>
        {rechte.canManage && daten && kontext && (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
            <KiAbleitungKnopf projectId={projectId} maske={daten} />
            <button
              type="button"
              className="text-xs hover:underline"
              style={{ color: 'var(--primary)' }}
              onClick={neuePosition}
            >
              {t('v2.staffing_maske.position_hinzufuegen', '+ Position hinzufügen')}
            </button>
          </div>
        )}
      </div>
      {!daten && query.isError && (
        <div className="text-xs" style={{ color: 'var(--danger)' }}>
          {t('v2.staffing_maske.laden_fehler')}
        </div>
      )}
      {!daten && !query.isError && (
        <div className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.staffing_maske.laden')}
        </div>
      )}
      {daten && kontext && (
        <MaskeKontext.Provider value={kontext}>
          <ZuBestaetigenKasten positionen={daten.positionen} kannBestaetigen={rechte.canManage} />
          <ZiehBereich>
            <NichtZugeordnet daten={daten.nicht_zugeordnet} />
            <div className="rounded" style={{ border: '1px solid var(--line-soft)' }}>
              {ganzLeer &&
                (rechte.canManage ? (
                  <div className="flex flex-wrap items-center gap-3 px-3 py-3">
                    <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
                      {t('v2.staffing_maske.leer_mit_recht')}
                    </span>
                    <button
                      type="button"
                      className="rounded px-3 py-1 text-sm font-medium"
                      style={{ background: 'var(--primary)', color: 'var(--on-primary)' }}
                      onClick={neuePosition}
                    >
                      {t('v2.staffing_maske.leer_knopf')}
                    </button>
                  </div>
                ) : (
                  <div className="px-3 py-3 text-sm" style={{ color: 'var(--ink-2)' }}>
                    {t('v2.staffing_maske.leer_ohne_recht')}
                  </div>
                ))}
              {!ganzLeer && bestaetigte.length === 0 && neuePositionen.length === 0 && (
                <div className="px-2 py-2 text-xs" style={{ color: 'var(--ink-3)' }}>
                  {t('v2.staffing_maske.leer')}
                </div>
              )}
              {bestaetigte.map((p) => (
                <PositionsZeile
                  key={p.beauftragte_position_id}
                  position={p}
                  anfangsOffen={aufgeklappt.includes(p.beauftragte_position_id)}
                />
              ))}
              {neuePositionen.map((n) => (
                <NeuePositionZeile
                  key={n}
                  onFertig={() => setNeuePositionen((l) => l.filter((x) => x !== n))}
                  onAngelegt={(id) => setAufgeklappt((l) => [...l, id])}
                />
              ))}
            </div>
            {daten.ehemalige.length > 0 && (
              <div>
                <button
                  type="button"
                  aria-expanded={ehemaligeOffen}
                  onClick={() => setEhemaligeOffen((v) => !v)}
                  className="text-xs hover:underline"
                  style={{ color: 'var(--ink-2)' }}
                >
                  {ehemaligeOffen ? '▾ ' : '▸ '}
                  {t('v2.staffing_maske.ehemalige', { n: daten.ehemalige.length })}
                </button>
                {ehemaligeOffen &&
                  daten.ehemalige.map((b) => (
                    <BesetzungZeile key={b.staffing_besetzung_id} b={b} />
                  ))}
              </div>
            )}
          </ZiehBereich>
        </MaskeKontext.Provider>
      )}
    </section>
  );
}
